import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Clear as ClearIcon,
  EmojiEmotions as EmojiIcon,
} from '@mui/icons-material';
import FormFieldRenderer from './FormFieldRenderer';
import ChatMessageRenderer from './ChatMessageRenderer';

const messageType = {
  answer: 'answer',
  question: 'question',
  form: 'form',
  system: 'system',
};

function RichChatInterface({
  messages = [],
  onSendMessage,
  onFormSubmit,
  onFileUpload,
  onVoiceInput,
  onEmojiClick,
  isProcessing = false,
  selectedModel = 'llama4',
  temperature = 0.7,
  playAudio = false,
  darkMode = false,
  messagesEndRef,
  inputRef,
  question = '',
  setQuestion,
  file = null,
  setFile,
  isListening = false,
  startListening,
  stopListening,
  sessionStartTime = Date.now(),
  flowState = null,
  onEndFlow = null,
}) {
  const theme = useTheme();
  const [formData, setFormData] = useState({});
  const [activeForm, setActiveForm] = useState(null);

  // Track which messages are new based on session start time
  const isNewMessage = (message) => {
    if (!message.timestamp) return false;
    const messageTime = new Date(message.timestamp).getTime();
    return messageTime >= sessionStartTime;
  };

  // Manual scroll to bottom function
  const scrollToBottom = () => {
    if (messagesEndRef?.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Auto-scroll to bottom when new messages are added
  const [prevMessageCount, setPrevMessageCount] = useState(0);

  useEffect(() => {
    // Only scroll if we have more messages than before (new message added)
    if (messages.length > prevMessageCount && messagesEndRef?.current) {
      // Add a small delay to ensure the message is rendered
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    setPrevMessageCount(messages.length);
  }, [messages.length, prevMessageCount, messagesEndRef]);

  // Auto-activate forms when they appear
  useEffect(() => {
    const formMessages = messages.filter((msg) => msg.type === messageType.form);
    if (formMessages.length > 0) {
      const latestForm = formMessages[formMessages.length - 1];
      if (activeForm !== latestForm.id) {
        setActiveForm(latestForm.id);
      }
    }
  }, [messages, activeForm]);

  // Handle form field changes
  const handleFormFieldChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // Handle form submission
  const handleFormSubmit = (formId, data) => {
    if (onFormSubmit) {
      // Use the actual formId from the message content, not the message ID
      const actualFormId = formId;
      onFormSubmit(actualFormId, data);
    }
    // Clear form data after a short delay to allow for processing
    setTimeout(() => {
      setFormData({});
      setActiveForm(null);
    }, 1000);
  };

  // Handle rich message sending
  const handleSendRichMessage = (content, type = 'question') => {
    if (onSendMessage) {
      const message = {
        content,
        type, // Use 'question' or 'answer' instead of 'text'
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
      };
      onSendMessage(message);
    }
  };

  // Render different types of messages
  const renderMessage = (message, index) => {
    switch (message.type) {
      case messageType.form:
        return (
          <ChatMessageRenderer
            key={message.id || index}
            message={message}
            onFormSubmit={handleFormSubmit}
            onFormFieldChange={handleFormFieldChange}
            formData={formData}
            isActive={activeForm === message.id}
            onActivate={() => setActiveForm(message.id)}
            isNewMessage={isNewMessage(message)}
            isProcessing={isProcessing}
            darkMode={darkMode}
          />
        );

      case messageType.system:
        return (
          <Box
            key={message.id || index}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              my: 1,
            }}
          >
            <Chip
              label={message.content}
              size="small"
              color="info"
              variant="outlined"
            />
          </Box>
        );

      default:
        return (
          <ChatMessageRenderer
            key={message.id || index}
            message={message}
            darkMode={darkMode}
            isNewMessage={isNewMessage(message)}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.default',
      }}
    >
      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.map((message, index) => renderMessage(message, index))}

        {/* Processing indicator */}
        {isProcessing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              AI is thinking...
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {/* File attachment indicator */}
        {file && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              📎
              {' '}
              {file.name}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setFile(null)}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Input controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          {/* Voice input - Hidden on mobile */}
          <Tooltip title={isListening ? 'Stop recording' : 'Voice input'}>
            <span>
              <IconButton
                onClick={isListening ? stopListening : startListening}
                color={isListening ? 'error' : 'default'}
                disabled={isProcessing}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' }, // Hide on mobile
                }}
              >
                {isListening ? <StopIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>

          {/* File upload - Hidden on mobile */}
          <Tooltip title="Attach file">
            <span>
              <IconButton
                component="label"
                disabled={isProcessing}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' }, // Hide on mobile
                }}
              >
                <AttachFileIcon />
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) setFile(file);
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* Emoji button - Hidden on mobile */}
          <Tooltip title="Add emoji">
            <span>
              <IconButton
                onClick={(event) => {
                  if (onEmojiClick) {
                    onEmojiClick(event);
                  }
                }}
                disabled={isProcessing}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' }, // Hide on mobile
                }}
              >
                <EmojiIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Flow Status Indicator */}
          {flowState && flowState.isActive && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            p: 1,
            bgcolor: flowState.waitingForForm ? 'warning.light' : 'primary.light',
            borderRadius: 1,
            color: flowState.waitingForForm ? 'warning.contrastText' : 'primary.contrastText',
          }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {flowState.waitingForForm ? '⏸️ Flow Paused' : '🔄 Active Flow'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {flowState.waitingForForm ? 'Waiting for form input' : 'Running'}
            </Typography>
            {onEndFlow && (
              <IconButton
                size="small"
                onClick={onEndFlow}
                sx={{
                  color: 'error.light',
                  ml: 'auto',
                  '&:hover': { bgcolor: 'error.main' },
                }}
                title="End Flow"
              >
                <StopIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          )}

          {/* Text input */}
          <Box sx={{ flex: 1 }}>
            <FormFieldRenderer
              type="textarea"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Type your message or ask a question..."
              disabled={isProcessing}
              multiline
              rows={1}
              maxRows={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (question.trim() && !isProcessing) {
                    handleSendRichMessage(question.trim());
                    setQuestion('');
                  }
                }
              }}
            />
          </Box>

          {/* Send button */}
          <Tooltip title="Send message">
            <span>
              <IconButton
                onClick={() => {
                  if (question.trim() && !isProcessing) {
                    handleSendRichMessage(question.trim());
                    setQuestion('');
                  }
                }}
                disabled={!question.trim() || isProcessing}
                color="primary"
              >
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

export default RichChatInterface;
